import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  InputAdornment,
  IconButton,
  Box,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { authApi } from '@/features/auth';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN } from '@/constants/auth';
import PasswordCheck from '@/features/auth/components/PasswordCheck';

interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const { t } = useTranslation('auth');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    mode: 'onChange',
  });

  const newPassword = watch('newPassword', '');

  // Password validation checks
  const passwordChecks = {
    minLength: newPassword.length >= PASSWORD_MIN_LENGTH,
    uppercase: PASSWORD_PATTERN.uppercase.test(newPassword),
    lowercase: PASSWORD_PATTERN.lowercase.test(newPassword),
    number: PASSWORD_PATTERN.number.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      toast.success(t('changePassword.successMessage'));
      handleClose();
    },
    onError: (error: Error & { response?: { status?: number } }) => {
      if (error.response?.status === 400) {
        toast.error(t('changePassword.error.wrongPassword'));
      } else if (error.response?.status === 403) {
        toast.error(t('changePassword.error.oauthOnly'));
      } else {
        toast.error(t('changePassword.error.wrongPassword'));
      }
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  const handleClose = () => {
    reset();
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('changePassword.title')}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              fullWidth
              type={showCurrentPassword ? 'text' : 'password'}
              label={t('changePassword.currentPassword')}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              {...register('currentPassword', {
                required: t('changePassword.validation.currentRequired'),
              })}
            />

            <TextField
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              label={t('changePassword.newPassword')}
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: newPassword ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              {...register('newPassword', {
                required: t('changePassword.validation.newRequired'),
                validate: () => isPasswordValid || t('changePassword.validation.passwordWeak'),
              })}
            />

            {/* Password requirements */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: -1 }}>
              <PasswordCheck
                isValid={passwordChecks.minLength}
                label={t('password.minLength')}
                iconSize={14}
              />
              <PasswordCheck
                isValid={passwordChecks.uppercase}
                label={t('password.uppercase')}
                iconSize={14}
              />
              <PasswordCheck
                isValid={passwordChecks.lowercase}
                label={t('password.lowercase')}
                iconSize={14}
              />
              <PasswordCheck
                isValid={passwordChecks.number}
                label={t('password.number')}
                iconSize={14}
              />
            </Box>

            <TextField
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              label={t('changePassword.confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        size="small"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              {...register('confirmPassword', {
                required: t('changePassword.validation.confirmRequired'),
                validate: (value) =>
                  value === newPassword || t('changePassword.validation.passwordMismatch'),
              })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            {t('changePassword.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={changePasswordMutation.isPending || !isPasswordValid}
          >
            {changePasswordMutation.isPending
              ? t('changePassword.changing')
              : t('changePassword.changeButton')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
